/**
 * ParentTeenVaultPage.js — parent-side Teen Vault hub.
 * Live view = ONE unified Mint Queue: every trophy any teen has ticked for
 * minting, each with a big round green Mint button. Viewing the full NFT
 * collection happens inside the interactive vault (demo view).
 */
import React, { useEffect, useState } from 'react';
import { Lock, Loader2, KeyRound, ArrowLeft, Trophy, CheckCircle2 } from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';
import VaultDemo from './VaultDemo';

const TrophyThumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-200 overflow-hidden flex-shrink-0 shadow flex items-center justify-center">
      {src && !broken
        ? <img src={src} alt={alt} className="w-full h-full object-cover" onError={() => setBroken(true)} />
        : <Trophy size={26} className="text-emerald-600" />}
    </div>
  );
};

const ParentTeenVaultPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [view, setView] = useState('live');   // 'live' | 'demo'

  const load = async () => {
    try {
      const res = await apiClient.request('/api/parent/custom-dashboard');
      setData(res);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const token = localStorage.getItem('hibff_auth_token');
    if (!token || typeof EventSource === 'undefined') return undefined;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    let es;
    try {
      es = new EventSource(`${apiUrl}/api/teen/milestones/stream?token=${encodeURIComponent(token)}`);
      es.addEventListener('vault_choice_made', load);
    } catch (e) { /* */ }
    return () => { try { es && es.close(); } catch (e) { /* */ } };
  }, []);

  // Big green button — parent mints a trophy the teen ticked in their Vault.
  const mintForTeen = async (teenId, memeId) => {
    setExecuting(memeId);
    try {
      const r = await apiClient.request('/api/memes/parent-mint', {
        method: 'POST', body: JSON.stringify({ teen_id: teenId, meme_id: memeId, chain: 'polygon' }),
      });
      alert(r?.message || 'Minting started!');
      load();
    } catch (e) { alert(e?.message || 'Minting failed'); }
    finally { setExecuting(null); }
  };

  // One flat queue: every ticked trophy across all teens.
  const teens = data?.teens || [];
  const queue = teens.flatMap((t) =>
    (t.vault?.mint_requests || []).map((m) => ({ ...m, teen_id: t.teen_id, teen_name: t.teen_first_name }))
  );
  const waitingTeens = teens
    .filter((t) => (t.vault?.mint_requests || []).length === 0)
    .map((t) => t.teen_first_name);

  return (
    <ParentShell activeId="teen-vault" title="Teen Vault">
      {view === 'demo' ? (
        <div className="px-2 lg:px-4 py-6">
          <button
            onClick={() => setView('live')}
            data-testid="vault-back-btn"
            className="ml-4 lg:ml-8 mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm text-sm font-semibold text-gray-700 transition"
          >
            <ArrowLeft size={14} /> Back to Live Vault
          </button>
          <VaultDemo embedded />
        </div>
      ) : (
        <div className="px-6 lg:px-10 py-8 lg:py-10 space-y-6 max-w-4xl mx-auto">
          {/* Prominent pill — opens the interactive vault where all the teen's NFTs are displayed */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              onClick={() => setView('demo')}
              data-testid="open-vault-pill"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-white shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
              style={{
                background: 'linear-gradient(135deg,#F5831F 0%,#FF6B35 50%,#7B4FD8 120%)',
                letterSpacing: '0.02em',
              }}
            >
              <KeyRound size={20} />
              Open The Vault
              <span className="text-xs font-semibold opacity-80 ml-1">— see every NFT</span>
            </button>
            <p className="text-sm text-gray-500">
              All earned NFTs live inside the vault.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" size={28} /></div>
          ) : (
            <>
              {/* Slim vault clock strip */}
              <section className="rounded-2xl bg-gradient-to-r from-slate-900 to-indigo-950 text-white px-6 py-4 shadow-lg flex items-center justify-between gap-4" data-testid="vault-summary">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
                    <Lock size={18} className="text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm font-bold leading-tight">180-Day Vault Clock</h2>
                    <p className="text-[11px] text-indigo-200 truncate">
                      {data?.global_vault?.matured_at_label || 'Character NFTs fully reveal to your teen when the countdown hits zero.'}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-3xl font-black text-amber-300 font-mono leading-none" data-testid="vault-days-left">
                    {String(data?.global_vault?.days_left ?? 180).padStart(3, '0')}
                  </div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-indigo-200">days left</p>
                </div>
              </section>

              {/* THE page: one unified mint queue */}
              <section className="rounded-3xl bg-white border border-gray-100 shadow-sm p-7" data-testid="mint-queue">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xl font-bold text-gray-900">Mint Queue</h3>
                  {queue.length > 0 && (
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
                      {queue.length} awaiting your action
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-6">
                  Trophies your teens ticked to become real NFTs. Press the green button to mint on Crossmint.
                </p>

                {queue.length === 0 ? (
                  <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-8 text-center" data-testid="mint-queue-empty">
                    <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
                    <p className="text-sm font-bold text-emerald-900">Queue is clear</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      When a teen ticks a trophy in their Vault, it lands here with a big green mint button.
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {queue.map((m) => (
                      <li key={`${m.teen_id}-${m.meme_id}`}
                        className="flex items-center gap-5 p-4 rounded-2xl bg-emerald-50 border-2 border-emerald-200"
                        data-testid={`mint-queue-item-${m.meme_id}`}>
                        <TrophyThumb src={m.image_url} alt={m.title} />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 truncate">{m.title}</p>
                          <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-800 text-[11px] font-bold">
                            Ticked by {m.teen_name}
                          </span>
                        </div>
                        {/* Big round glossy green mint button */}
                        <button
                          onClick={() => mintForTeen(m.teen_id, m.meme_id)}
                          disabled={executing === m.meme_id}
                          data-testid={`parent-mint-${m.meme_id}`}
                          title="Mint on Crossmint"
                          className="relative w-24 h-24 rounded-full flex-shrink-0 flex flex-col items-center justify-center text-white font-black transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 focus:outline-none"
                          style={{
                            background: 'radial-gradient(circle at 32% 26%, #86efac 0%, #22c55e 38%, #16a34a 62%, #15803d 82%, #14532d 100%)',
                            border: '4px solid #166534',
                            boxShadow: '0 10px 24px rgba(21,128,61,.45), inset 0 -8px 14px rgba(0,0,0,.3), inset 0 5px 10px rgba(255,255,255,.35)',
                          }}
                        >
                          <span className="pointer-events-none absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-6 rounded-full bg-white/40" style={{ filter: 'blur(5px)' }} />
                          {executing === m.meme_id ? (
                            <Loader2 size={26} className="animate-spin" />
                          ) : (
                            <>
                              <span className="text-lg leading-none drop-shadow">MINT</span>
                              <span className="text-[9px] font-bold opacity-90 mt-1 leading-none">on Crossmint</span>
                            </>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {queue.length > 0 && waitingTeens.length > 0 && (
                  <p className="mt-5 text-xs text-gray-400" data-testid="mint-queue-waiting">
                    Nothing ticked yet from: {waitingTeens.join(', ')}
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </ParentShell>
  );
};

export default ParentTeenVaultPage;
